import { CopropertyDto } from 'api/coproperty';
import { GenericMessage } from 'components/feedback/Message';
import { Button } from 'components/ui/Button';
import {
  CopropertySurveyEngine,
  SurveyStepControls
} from 'features/subscription-process/coproperty/CopropertySurveyEngine';
import { usePanel } from 'hooks/usePanel';
import { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRefetchQuoteData } from '../../../hooks/useRefetchQuoteData';
import { useQuoteContractualizationData } from '../../../store';

interface EditCopropertySurveyPanelProps {
  /** Copropriété du devis, telle que la contractualisation la renvoie. */
  coproperty: CopropertyDto;
  /** Dossier et devis en cours : ils portent les réponses déjà enregistrées. */
  riskId: string;
  quoteId?: string;
}

/**
 * Sous-panneau « Questionnaire d'appréciation du risque » de la contractualisation MRI :
 * le moteur du parcours rejoué ici, bloc par bloc, avec les réponses déjà enregistrées.
 * Le panneau est rendu hors de l'arbre des routes : il relit les réponses par le process
 * de souscription plutôt que par le contexte, et un refus du serveur reste un toast — on
 * ne sort pas un dossier déjà tarifé du parcours depuis la contractualisation.
 */
export const EditCopropertySurveyPanel: FC<EditCopropertySurveyPanelProps> = ({ coproperty, riskId, quoteId }) => {
  const { closePanel, setPanelActions } = usePanel();
  const refetchQuoteData = useRefetchQuoteData();
  const [controls, setControls] = useState<SurveyStepControls | null>(null);
  // Réponses de la session COPRO_MRI, portées par les données de contractualisation
  // (rafraîchies après chaque enregistrement par refetchQuoteData).
  const savedAnswers = useQuoteContractualizationData()?.surveyAnswers ?? undefined;
  const handleControlsChange = useCallback((next: SurveyStepControls) => setControls(next), []);

  const handleCompleted = useCallback(async () => {
    await refetchQuoteData();
    closePanel();
  }, [refetchQuoteData, closePanel]);

  const handleRefused = useCallback((reasons: string[]) => {
    toast.error(
      reasons.length > 0
        ? `Avec ces réponses, la copropriété n'est pas assurable : ${reasons.join(' ')}`
        : "Avec ces réponses, la copropriété n'est pas assurable."
    );
  }, []);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'Fermer',
      submitLabel: controls?.nextLabel ?? 'Bloc suivant',
      submitDisabled: controls?.nextDisabled ?? true,
      onSubmit: () => controls?.onNext()
    });
  }, [setPanelActions, controls]);


  return (
    <CopropertySurveyEngine
      coproperty={coproperty}
      savedAnswers={savedAnswers}
      onCompleted={handleCompleted}
      onRefused={handleRefused}
      onControlsChange={handleControlsChange}
    >
      {({ rail, message, content }) => (
        <div className="flex flex-col gap-4">
          <GenericMessage message={message} type="info" dismissible={false} />
          {rail}
          {content}
          {controls && !controls.prevHidden && (
            <div>
              <Button outline color="primary" onClick={controls.onPrevious}>
                {controls.prevLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </CopropertySurveyEngine>
  );
};
